import React from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Button } from './Button';

// Shared building blocks for the "Xem chi tiết" modals so every entity reads the same way.

export interface DetailHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
}

export function DetailHeader({ icon, title, subtitle, badge }: DetailHeaderProps) {
  return (
    <div className="flex min-w-0 items-start gap-4">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-ink-100 text-base font-semibold text-ink-700">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="break-words text-lg font-semibold text-ink-900 sm:text-xl">{title}</h3>
        {subtitle ? <p className="mt-0.5 text-sm text-ink-500">{subtitle}</p> : null}
        {badge ? <div className="mt-2">{badge}</div> : null}
      </div>
    </div>
  );
}

export interface DetailSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

export function DetailSection({ title, children, className }: DetailSectionProps) {
  return (
    <section className={cn('min-w-0', className)}>
      <h4 className="mb-2.5 border-b border-ink-200 pb-1.5 text-sm font-semibold text-ink-900">{title}</h4>
      {children}
    </section>
  );
}

export function DetailList({ children }: { children: React.ReactNode }) {
  return <dl className="space-y-3">{children}</dl>;
}

export interface DetailItemProps {
  label: string;
  value?: React.ReactNode;
  icon?: React.ReactNode;
  // Shown in muted text when the value is empty.
  fallback?: string;
}

export function DetailItem({ label, value, icon, fallback = 'Chưa có' }: DetailItemProps) {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className="flex min-w-0 items-start gap-3">
      {icon ? <span className="mt-0.5 shrink-0 text-ink-400 [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
      <div className="min-w-0 flex-1">
        <dt className="text-xs text-ink-500">{label}</dt>
        <dd className={cn('break-words text-sm', isEmpty ? 'text-ink-400' : 'text-ink-900')}>
          {isEmpty ? fallback : value}
        </dd>
      </div>
    </div>
  );
}

export interface DetailStat {
  label: string;
  value: React.ReactNode;
}

export function DetailStats({ items }: { items: DetailStat[] }) {
  return (
    <div className={cn('grid divide-x divide-ink-200 rounded-lg border border-ink-200', items.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : items.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
      {items.map(item => (
        <div key={item.label} className="px-3 py-2.5">
          <p className="text-xs text-ink-500">{item.label}</p>
          <p className="text-lg font-semibold tabular-nums text-ink-900">{item.value}</p>
        </div>
      ))}
    </div>
  );
}

export interface DetailFooterProps {
  onClose: () => void;
  // Omit when the viewer can't edit; only "Đóng" is shown then.
  onEdit?: () => void;
}

export function DetailFooter({ onClose, onEdit }: DetailFooterProps) {
  return (
    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button variant="secondary" onClick={onClose}>Đóng</Button>
      {onEdit ? (
        <Button icon={<Pencil className="h-4 w-4" />} onClick={onEdit}>Chỉnh sửa</Button>
      ) : null}
    </div>
  );
}
